// src/pages/creator-panel/LiveConversation/Tools/ToolsList.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../../../utils/axiosInstance';
import { useChatbots } from '../../../../contexts/ChatbotContext';
import { hasPermission } from '../../../../utils/permissionUtils';

import {
    Box,
    Typography,
    Button,
    Alert,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Divider,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as TestIcon,
    History as LogsIcon,
    Build as ToolIcon,
} from '@mui/icons-material';

import ToolLogsDrawer from './ToolLogsDrawer';

const methodColors = {
    GET: 'success',
    POST: 'primary',
    PUT: 'warning',
    PATCH: 'warning',
    DELETE: 'error',
};

export default function ToolsList() {
    const navigate = useNavigate();
    const { chatbotId: urlChatbotId } = useParams();
    const { activeChatbot } = useChatbots();
    const chatbotId = activeChatbot?.chatbot_identity;
    const scopedId = urlChatbotId || chatbotId;
    const baseToolsPath = `/chatbot/${scopedId}/tool-call`;

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    const [tools, setTools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);

    const [confirmDeleteTool, setConfirmDeleteTool] = useState(null);
    const [testDialog, setTestDialog] = useState(null); // {tool, paramValues, response, loading}
    const [logsDrawerTool, setLogsDrawerTool] = useState(null);

    useEffect(() => {
        (async () => {
            setCheckingPermissions(true);
            try {
                const [v, m, d] = await Promise.all([
                    hasPermission('live-conversation-tools', 'view'),
                    hasPermission('live-conversation-tools', 'add|edit'),
                    hasPermission('live-conversation-tools', 'delete'),
                ]);
                setCanView(v);
                setCanModify(m);
                setCanDelete(d);
            } catch (e) {
                console.error('permission check failed', e);
            } finally {
                setCheckingPermissions(false);
            }
        })();
    }, []);

    const fetchTools = useCallback(async () => {
        if (!chatbotId) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/chatbots/${chatbotId}/live-conversation/tools`);
            setTools(res.data?.data || []);
        } catch (e) {
            console.error(e);
            setMessage({ type: 'error', text: e?.response?.data?.message || 'Failed to load tools' });
        } finally {
            setLoading(false);
        }
    }, [chatbotId]);

    useEffect(() => {
        if (chatbotId && canView) fetchTools();
    }, [chatbotId, canView, fetchTools]);

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage(null), 4000);
    };

    const toggleFlag = async (tool, flag, value) => {
        try {
            const res = await axiosInstance.patch(`/api/live-conversation/tools/${tool.id}/toggle`, {
                [flag]: value,
            });
            setTools((prev) => prev.map((t) => (t.id === tool.id ? res.data.data : t)));
        } catch (e) {
            showMessage('error', e?.response?.data?.message || 'Toggle failed');
        }
    };

    const handleDelete = async () => {
        if (!confirmDeleteTool) return;
        try {
            await axiosInstance.delete(`/api/live-conversation/tools/${confirmDeleteTool.id}`);
            setTools((prev) => prev.filter((t) => t.id !== confirmDeleteTool.id));
            showMessage('success', 'Tool deleted');
        } catch (e) {
            showMessage('error', e?.response?.data?.message || 'Delete failed');
        } finally {
            setConfirmDeleteTool(null);
        }
    };

    const openTestDialog = (tool) => {
        const initialValues = {};
        Object.keys(tool.parameters_schema || {}).forEach((k) => {
            initialValues[k] = '';
        });
        setTestDialog({ tool, paramValues: initialValues, response: null, loading: false });
    };

    const runTest = async () => {
        if (!testDialog) return;
        setTestDialog((d) => ({ ...d, loading: true, response: null }));
        try {
            const res = await axiosInstance.post(
                `/api/live-conversation/tools/${testDialog.tool.id}/test`,
                { parameters: testDialog.paramValues }
            );
            setTestDialog((d) => ({ ...d, response: res.data?.data, loading: false }));
        } catch (e) {
            setTestDialog((d) => ({
                ...d,
                response: { success: false, error: e?.response?.data?.message || 'Request failed' },
                loading: false,
            }));
        }
    };

    if (checkingPermissions) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }
    if (!canView) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="warning">You don't have permission to view Tool Call.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <ToolIcon color="primary" />
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="h5" fontWeight={600}>Tool Call</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Connect custom HTTP APIs. The AI will call a tool when the guest asks something matching its description.
                    </Typography>
                </Box>
                {canModify && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`${baseToolsPath}/new`)}
                    >
                        Create Tool
                    </Button>
                )}
            </Box>

            {message && (
                <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : tools.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <ToolIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="h6">No tools yet</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Create your first tool to let the chatbot call your APIs.
                    </Typography>
                    {canModify && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${baseToolsPath}/new`)}>
                            Create Tool
                        </Button>
                    )}
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Endpoint</TableCell>
                                <TableCell align="center">Active</TableCell>
                                <TableCell align="center">Voice</TableCell>
                                <TableCell align="center">Text</TableCell>
                                <TableCell align="center">Phone</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tools.map((t) => (
                                <TableRow key={t.id} hover>
                                    <TableCell>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {t.display_name || t.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            {t.name}
                                        </Typography>
                                        {t.description && (
                                            <Typography variant="caption" color="text.secondary"
                                                sx={{ display: 'block', maxWidth: 320, whiteSpace: 'nowrap',
                                                    overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {t.description}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" color={methodColors[t.http_method] || 'default'} label={t.http_method} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace',
                                            maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap', display: 'inline-block' }}>
                                            {t.endpoint_url}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch size="small" checked={t.is_active}
                                            disabled={!canModify}
                                            onChange={(e) => toggleFlag(t, 'is_active', e.target.checked)} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch size="small" checked={t.enabled_for_voice}
                                            disabled={!canModify}
                                            onChange={(e) => toggleFlag(t, 'enabled_for_voice', e.target.checked)} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch size="small" checked={t.enabled_for_text}
                                            disabled={!canModify}
                                            onChange={(e) => toggleFlag(t, 'enabled_for_text', e.target.checked)} />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch size="small" checked={t.enabled_for_phone}
                                            disabled={!canModify}
                                            onChange={(e) => toggleFlag(t, 'enabled_for_phone', e.target.checked)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Test fire">
                                            <IconButton size="small" onClick={() => openTestDialog(t)}>
                                                <TestIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Call logs">
                                            <IconButton size="small" onClick={() => setLogsDrawerTool(t)}>
                                                <LogsIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {canModify && (
                                            <Tooltip title="Edit">
                                                <IconButton size="small"
                                                    onClick={() => navigate(`${baseToolsPath}/${t.id}/edit`)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {canDelete && (
                                            <Tooltip title="Delete">
                                                <IconButton size="small" color="error"
                                                    onClick={() => setConfirmDeleteTool(t)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Delete confirm */}
            <Dialog open={!!confirmDeleteTool} onClose={() => setConfirmDeleteTool(null)}>
                <DialogTitle>Delete Tool?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <b>{confirmDeleteTool?.display_name || confirmDeleteTool?.name}</b>?
                        All call logs will also be removed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteTool(null)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Test dialog */}
            <Dialog open={!!testDialog} onClose={() => setTestDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>Test &quot;{testDialog?.tool?.display_name || testDialog?.tool?.name}&quot;</DialogTitle>
                <DialogContent>
                    {testDialog && Object.keys(testDialog.tool.parameters_schema || {}).length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                            This tool has no parameters.
                        </Typography>
                    ) : (
                        Object.entries(testDialog?.tool?.parameters_schema || {}).map(([pname, pdef]) => (
                            <TextField
                                key={pname}
                                label={`${pname} (${pdef.type || 'string'})${pdef.required ? ' *' : ''}`}
                                helperText={pdef.description || ''}
                                fullWidth
                                sx={{ mb: 2 }}
                                value={testDialog.paramValues[pname] || ''}
                                onChange={(e) => setTestDialog((d) => ({
                                    ...d, paramValues: { ...d.paramValues, [pname]: e.target.value },
                                }))}
                            />
                        ))
                    )}

                    {testDialog?.loading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                            <CircularProgress size={18} /> <Typography variant="body2">Firing request…</Typography>
                        </Box>
                    )}

                    {testDialog?.response && (
                        <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 1 }} />
                            <Typography variant="subtitle2">
                                Result: {testDialog.response.success ? '✅ Success' : '❌ Failed'}
                                {testDialog.response.status_code != null && ` · HTTP ${testDialog.response.status_code}`}
                                {testDialog.response.latency_ms != null && ` · ${testDialog.response.latency_ms}ms`}
                            </Typography>
                            {testDialog.response.error && (
                                <Alert severity="error" sx={{ my: 1 }}>{testDialog.response.error}</Alert>
                            )}
                            <TextField
                                multiline
                                fullWidth
                                minRows={4}
                                maxRows={12}
                                value={testDialog.response.body || ''}
                                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestDialog(null)}>Close</Button>
                    <Button variant="contained" onClick={runTest} disabled={testDialog?.loading}>
                        Run Test
                    </Button>
                </DialogActions>
            </Dialog>

            <ToolLogsDrawer
                open={!!logsDrawerTool}
                onClose={() => setLogsDrawerTool(null)}
                tool={logsDrawerTool}
                chatbotId={chatbotId}
            />
        </Box>
    );
}
